import type { BackendModule, ReadCallback, Services } from 'i18next';
import httpData from '../utils/httpData';

/**
 * Cache configuration
 */
const CACHE_PREFIX = 'i18n_translations_';
const CACHE_VERSION_KEY = 'i18n_cache_version';
const CACHE_TTL = 60 * 60 * 1000; // 1 hour in milliseconds

interface CachedTranslations {
  data: Record<string, string>;
  timestamp: number;
  version: string;
}

/**
 * Custom i18next backend that loads translations from the API
 * with localStorage caching for performance
 */
class ApiBackend implements BackendModule<object> {
  type: 'backend' = 'backend';

  private baseUrl: string;
  private cache: Map<string, CachedTranslations> = new Map();

  constructor() {
    this.baseUrl = httpData.url || '';
  }

  init(_services: Services, _backendOptions: object): void {
    // Load cached data from localStorage on init
    this.loadCacheFromStorage();
  }

  read(language: string, namespace: string, callback: ReadCallback): void {
    // For now, we use a single namespace approach
    // The API returns all translations for a language
    if (namespace !== 'translation') {
      callback(null, {});
      return;
    }

    this.loadTranslations(language)
      .then((translations) => {
        callback(null, translations);
      })
      .catch((error) => {
        console.error(`Failed to load translations for ${language}:`, error);
        // Try to use cached data as fallback
        const cached = this.getCachedTranslations(language);
        if (cached) {
          callback(null, cached);
        } else {
          callback(error, null);
        }
      });
  }

  /**
   * Load translations from API or cache
   */
  private async loadTranslations(language: string): Promise<Record<string, string>> {
    // Check if we have valid cached data
    const cached = this.getCachedTranslations(language);
    if (cached && this.isCacheValid(language)) {
      return cached;
    }

    // Fetch from API
    const url = `${this.baseUrl}/i18n/${language}`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: Failed to fetch translations`);
    }

    const data = await response.json();

    // Cache the translations
    this.cacheTranslations(language, data.translations, data.version);

    return data.translations;
  }

  /**
   * Get cached translations for a language
   */
  private getCachedTranslations(language: string): Record<string, string> | null {
    const cached = this.cache.get(language);
    return cached?.data || null;
  }

  /**
   * Check if cache is still valid
   */
  private isCacheValid(language: string): boolean {
    const cached = this.cache.get(language);
    if (!cached) return false;

    const now = Date.now();
    return now - cached.timestamp < CACHE_TTL;
  }

  /**
   * Cache translations in memory and localStorage
   */
  private cacheTranslations(
    language: string,
    translations: Record<string, string>,
    version: string
  ): void {
    const cacheEntry: CachedTranslations = {
      data: translations,
      timestamp: Date.now(),
      version,
    };

    // Cache in memory
    this.cache.set(language, cacheEntry);

    // Cache in localStorage
    try {
      localStorage.setItem(
        `${CACHE_PREFIX}${language}`,
        JSON.stringify(cacheEntry)
      );
    } catch (e) {
      console.warn('Failed to cache translations in localStorage:', e);
    }
  }

  /**
   * Load cached data from localStorage on init
   */
  private loadCacheFromStorage(): void {
    try {
      const keys = Object.keys(localStorage).filter((key) =>
        key.startsWith(CACHE_PREFIX)
      );

      for (const key of keys) {
        const language = key.replace(CACHE_PREFIX, '');
        const cached = localStorage.getItem(key);

        if (cached) {
          const parsed: CachedTranslations = JSON.parse(cached);
          this.cache.set(language, parsed);
        }
      }
    } catch (e) {
      console.warn('Failed to load cached translations:', e);
    }
  }

  /**
   * Clear all cached translations
   */
  clearCache(): void {
    this.cache.clear();

    try {
      const keys = Object.keys(localStorage).filter((key) =>
        key.startsWith(CACHE_PREFIX)
      );

      for (const key of keys) {
        localStorage.removeItem(key);
      }
    } catch (e) {
      console.warn('Failed to clear translation cache:', e);
    }
  }

  /**
   * Force refresh translations for a language
   */
  async refreshTranslations(language: string): Promise<void> {
    // Clear cache for this language
    this.cache.delete(language);

    try {
      localStorage.removeItem(`${CACHE_PREFIX}${language}`);
    } catch (e) {
      // Ignore
    }

    // Reload from API
    await this.loadTranslations(language);
  }
}

// Export singleton instance
export const apiBackend = new ApiBackend();

export default ApiBackend;
